import { useSyncExternalStore } from "react";

/**
 * The radius preset: `<html data-radius="square|rounded|full">` picks the
 * token values in the theme CSS (`:root[data-radius]`). applyRadius switches
 * it; useRadiusPreset reads it for the few places that change structure
 * rather than classes — e.g. the sidebar's inset variant in Full. Pure
 * styling should use the `radius-square:` / `radius-full:` variants.
 */

export type RadiusPreset = "square" | "rounded" | "full";

export const RADIUS_CHANGE_EVENT = "radius-change";

/**
 * Switches the radius preset. Unknown keys (the legacy "default") fall back
 * to Rounded. Also clears the inline --radius older builds wrote, which would
 * otherwise beat the preset rules.
 */
export function applyRadius(key: string | null) {
  const root = document.documentElement;
  root.style.removeProperty("--radius");
  root.dataset.radius = key === "square" || key === "full" ? key : "rounded";
  window.dispatchEvent(new Event(RADIUS_CHANGE_EVENT));
}

function getSnapshot(): RadiusPreset {
  const value = document.documentElement.dataset.radius;
  return value === "square" || value === "full" ? value : "rounded";
}

function subscribe(listener: () => void) {
  window.addEventListener(RADIUS_CHANGE_EVENT, listener);
  return () => window.removeEventListener(RADIUS_CHANGE_EVENT, listener);
}

export function useRadiusPreset(): RadiusPreset {
  return useSyncExternalStore(subscribe, getSnapshot, () => "rounded");
}
